import { useEffect, useMemo, useState } from 'react'
import { useQuery } from 'react-query'
import { CiSearch } from 'react-icons/ci'
import { FiInfo } from 'react-icons/fi'
import posthog from 'posthog-js'
import DotsLoading from 'components/loadings/dotsLoading'
import { getLoanCandidates } from 'services/fund'
import { faPlain } from 'utilities/helper/format'
import CandidateRow from '../components/CandidateRow'
import { ScoreExplainer } from 'components/memberScore'

/**
 * A filter as a pill rather than a checkbox row with a two-line hint.
 *
 * The explanations matter, but spelled out they pushed the first candidate
 * 380px down a 932px screen — the list is the screen, and a manager scrolling
 * past a wall of help text to reach it is the failure mode. The «چرا؟» keeps
 * the wording, one tap away.
 */
function FilterPill({
    checked,
    onChange,
    label,
}: {
    checked: boolean
    onChange: (next: boolean) => void
    label: string
}) {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            aria-label={label}
            onClick={() => onChange(!checked)}
            className={` flex-1 rounded-full border py-1.5 text-[11px] font-bold transition-colors ${
                checked
                    ? ' border-brand bg-brand-soft text-brand-deep'
                    : ' border-stroke text-text-secondary'
            }`}
        >
            {label}
        </button>
    )
}

/**
 * Step 1 — «به چه کسی؟».
 *
 * This is the question the old screen never asked. It opened on «مبلغ وام»,
 * which is the second question; the first one a manager of a 500-member fund
 * answers from memory, standing in front of the person asking.
 *
 * So the list is ranked by امتیاز عضو and the two things that change the
 * shape of the list are checkboxes, not settings: whether to look past the
 * request queue, and whether to count old money at what it was worth.
 */
export default function StepBorrower({
    moneypoolId,
    selectedPoolshipId,
    onSelect,
}: {
    moneypoolId?: string
    selectedPoolshipId?: number
    onSelect: (candidate: ILoanCandidate) => void
}) {
    const [requestsOnly, setRequestsOnly] = useState(true)
    const [inflation, setInflation] = useState(false)
    const [search, setSearch] = useState('')
    const [showFilterHelp, setShowFilterHelp] = useState(false)
    const [explaining, setExplaining] = useState<ILoanCandidate | null>(null)

    const { data, isLoading, isError } = useQuery(
        ['loan-candidates', moneypoolId, requestsOnly, inflation],
        getLoanCandidates,
        { enabled: !!moneypoolId, staleTime: 60_000, keepPreviousData: true }
    )

    const rows = useMemo(() => {
        const results = data?.results ?? []
        const term = search.trim()
        if (!term) return results
        return results.filter((row) => row.member_name.includes(term))
    }, [data, search])

    // One event per search, once the typing stops — a capture per keystroke
    // would drown the funnel in a name's worth of events. What is worth
    // knowing is whether the search found anybody, which is why it waits for
    // the settled query rather than firing on the first character.
    //
    // The typed name never leaves the device: only how long it was, and how
    // many rows it matched.
    useEffect(() => {
        const term = search.trim()
        if (!term) return
        const timer = setTimeout(() => {
            posthog.capture('loan_candidates_searched', {
                fund_id: moneypoolId,
                query_length: term.length,
                result_count: rows.length,
                requests_only: requestsOnly,
                // a truncated list is the case where searching is the only way
                // to reach a member at all
                truncated: !!data?.truncated,
            })
        }, 700)
        return () => clearTimeout(timer)
    }, [search, rows.length, moneypoolId, requestsOnly, data?.truncated])

    if (isLoading && !data) {
        return (
            <div className=" py-10">
                <DotsLoading />
            </div>
        )
    }

    if (isError) {
        return (
            <div className=" rounded-2xl border border-stroke bg-white p-4 text-center">
                <p className=" text-xs leading-6 text-text-secondary">
                    فهرست اعضا الان در دسترس نیست. می‌توانید دوباره تلاش کنید.
                </p>
            </div>
        )
    }

    // keyed off the rows we actually got, not the demands counter: if the
    // two ever disagree, hiding real candidates behind «هیچ درخواستی ندارید»
    // is the worse failure
    const emptyQueue = requestsOnly && (data?.results.length ?? 0) === 0

    return (
        <>
            <div className=" rounded-2xl border border-stroke bg-white p-3">
                <div className=" flex items-center gap-2 rounded-xl bg-canvas px-3 py-2">
                    <CiSearch size={18} className=" shrink-0 text-text-muted" />
                    <input
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="جستجوی نام عضو"
                        aria-label="جستجوی نام عضو"
                        className=" w-full bg-transparent text-xs outline-none placeholder:text-text-muted"
                    />
                </div>

                <div className=" mt-2 flex items-center gap-2">
                    <FilterPill
                        checked={!requestsOnly}
                        onChange={(next) => {
                            setRequestsOnly(!next)
                            posthog.capture('loan_candidates_scope_changed', {
                                fund_id: moneypoolId,
                                include_all: next,
                            })
                        }}
                        label="همهٔ اعضا"
                    />
                    <FilterPill
                        checked={inflation}
                        onChange={(next) => {
                            setInflation(next)
                            posthog.capture('loan_candidates_inflation_toggled', {
                                fund_id: moneypoolId,
                                inflation: next,
                            })
                        }}
                        label="تعدیل تورمی"
                    />
                    <button
                        type="button"
                        aria-label="راهنمای فیلترها"
                        onClick={() => setShowFilterHelp((open) => !open)}
                        className=" -m-1 shrink-0 p-1 text-text-muted"
                    >
                        <FiInfo size={15} />
                    </button>
                </div>
                {showFilterHelp && (
                    <div className=" mt-2 rounded-xl bg-canvas p-2.5 text-[11px] leading-5 text-text-secondary">
                        <p>
                            <b className=" text-text-body">همهٔ اعضا:</b>{' '}
                            به‌طور پیش‌فرض فقط کسانی را می‌بینید که درخواست وام
                            داده‌اند. با این گزینه، همهٔ اعضای صندوق نمایش داده
                            می‌شوند.
                        </p>
                        <p className=" mt-1.5">
                            <b className=" text-text-body">تعدیل تورمی:</b> ۱
                            میلیون تومان چند سال پیش بیشتر از ۱ میلیون امروز
                            بوده؛ با شاخص قیمت مصرف‌کننده به پول امروز تبدیل
                            می‌شود.
                        </p>
                    </div>
                )}
                {inflation && data && !data.inflation.applied && (
                    <p className=" mt-2 rounded-lg bg-warning-soft px-2 py-1.5 text-[11px] leading-5 text-warning-on">
                        شاخص تورم هنوز روی سرور بارگذاری نشده، پس فعلاً بدون
                        تعدیل محاسبه شده است.
                    </p>
                )}
            </div>

            <p className=" mt-3 px-1 text-[11px] text-text-secondary">
                {requestsOnly
                    ? `${faPlain.format(
                          data?.demands_count ?? 0
                      )} نفر درخواست وام داده‌اند`
                    : `${faPlain.format(
                          data?.members_count ?? 0
                      )} عضو، بر اساس امتیاز مرتب شده‌اند`}
            </p>

            {emptyQueue ? (
                <div className=" mt-2 rounded-2xl border border-stroke bg-white p-4 text-center">
                    <p className=" text-xs font-bold text-text-body">
                        هیچ درخواست وام بازی ندارید
                    </p>
                    <p className=" mt-1 text-[11px] leading-6 text-text-secondary">
                        برای اعطای وام به عضوی که حضوری درخواست داده،
                        «نمایش همهٔ اعضا» را روشن کنید.
                    </p>
                </div>
            ) : rows.length === 0 ? (
                <div className=" mt-2 rounded-2xl border border-stroke bg-white p-4 text-center">
                    <p className=" text-xs text-text-secondary">
                        عضوی با این نام پیدا نشد.
                    </p>
                </div>
            ) : (
                <div className=" mt-2 space-y-2">
                    {rows.map((candidate) => (
                        <CandidateRow
                            key={candidate.poolship_id}
                            candidate={candidate}
                            moneypoolId={moneypoolId}
                            isSelected={
                                candidate.poolship_id === selectedPoolshipId
                            }
                            onSelect={() => onSelect(candidate)}
                            onExplain={() => {
                                setExplaining(candidate)
                                posthog.capture('loan_score_explained', {
                                    fund_id: moneypoolId,
                                    poolship_id: candidate.poolship_id,
                                })
                            }}
                        />
                    ))}
                </div>
            )}

            {data?.truncated && (
                <p className=" mt-2 px-1 text-[11px] text-text-secondary">
                    فهرست طولانی است و فقط بخشی از آن نمایش داده شده؛ برای
                    یافتن عضو خاص از جستجو استفاده کنید.
                </p>
            )}

            <ScoreExplainer
                show={!!explaining}
                onHide={() => setExplaining(null)}
                candidate={explaining}
                overdueMultiplier={data?.overdue_multiplier ?? 3}
                earlyMultiplier={data?.early_multiplier ?? 3}
                membersCount={data?.members_count ?? 0}
                inflationApplied={!!data?.inflation.applied}
            />
        </>
    )
}
